import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { Category, GigListItem } from '../../types/content';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CatalogFilterLayout, { CatalogFilterSelect } from '../../components/content/CatalogFilterLayout';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroShell } from '../../content/heroThemes';

const formatPrice = (gig: GigListItem) => {
  if (gig.priceAmount == null) return 'Rate on request';
  const amount = new Intl.NumberFormat('en-IN', { style: 'currency', currency: gig.currency || 'INR', maximumFractionDigits: 0 }).format(gig.priceAmount);
  return gig.priceType === 'Hourly' ? `${amount} / hour` : amount;
};

const GigsPage: React.FC = () => {
  const [gigs, setGigs] = useState<GigListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [teamType, setTeamType] = useState('');
  const [priceType, setPriceType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    contentApi.listCategories('Gig').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    contentApi.listGigs({
      search: search || undefined,
      categoryId: categoryId || undefined,
      teamType: teamType || undefined,
      priceType: priceType || undefined,
      pageSize: 24,
    }).then((r) => {
      setGigs(r.data.gigs || []);
      setError(null);
    }).catch(() => setError('Unable to load gigs.')).finally(() => setLoading(false));
  }, [search, categoryId, teamType, priceType]);

  return (
    <div>
      <section className={heroShell('gigs')}>
        <HeroBackdrop tone="gigs" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Chances to earn</p>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl">Real gigs from clients</h1>
          <p className="mt-5 text-base sm:text-lg text-primary-100/95 max-w-2xl leading-relaxed">
            Gigs are paid opportunities posted by admins or clients — duration, weekly hours, tech stack, rate, and who can apply. Projects on this site are for learning; gigs are for earning.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/projects"><Button variant="outline" className="!text-white !border-white/40 hover:!bg-white/10" size="lg">Practice on learning projects<Briefcase className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/courses" className="inline-flex items-center text-sm font-medium text-primary-100 hover:text-white">
              Build skills first<ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CatalogFilterLayout
          applied={[
            ...(search.trim() ? [{ key: 'search', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') }] : []),
            ...(categoryId ? [{ key: 'category', label: `Category: ${categories.find((c) => String(c.id) === String(categoryId))?.name || categoryId}`, onRemove: () => setCategoryId('') }] : []),
            ...(teamType ? [{ key: 'who', label: `Who: ${teamType}`, onRemove: () => setTeamType('') }] : []),
            ...(priceType ? [{ key: 'price', label: `Price: ${priceType}`, onRemove: () => setPriceType('') }] : []),
          ]}
          onClearFilters={() => {
            setSearch('');
            setCategoryId('');
            setTeamType('');
            setPriceType('');
          }}
          results={(
            <>
              {loading && <PageLoading label="Loading gigs…" />}
              {error && <p className="p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}
              {!loading && !error && gigs.length === 0 && <p className="text-gray-500">No published gigs match these filters.</p>}
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {gigs.map((gig) => (
                  <Link key={gig.id} to={`/gigs/${gig.slug}`}>
                    <Card className="h-full overflow-hidden" hoverable>
                      {gig.coverImageUrl && <img src={gig.coverImageUrl} alt="" className="h-40 w-full object-cover" />}
                      <div className="p-5">
                        <p className="text-xs uppercase tracking-wide text-primary-600">{gig.clientName}</p>
                        <h2 className="mt-2 text-xl font-semibold text-gray-900">{gig.title}</h2>
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">{gig.subtitle || gig.summary}</p>
                        <p className="mt-4 text-sm font-semibold text-gray-900">{formatPrice(gig)}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {gig.teamType} · {gig.duration || 'Duration TBD'} · {gig.weeklyHours || 'Hours TBD'}
                        </p>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        >
          <Input id="gig-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search gigs" />
          <CatalogFilterSelect label="Category" value={categoryId} onChange={setCategoryId}>
            <option value="">All</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </CatalogFilterSelect>
          <CatalogFilterSelect label="Who" value={teamType} onChange={setTeamType}>
            <option value="">All</option>
            <option value="Individual">Individual</option>
            <option value="Team">Team</option>
          </CatalogFilterSelect>
          <CatalogFilterSelect label="Price" value={priceType} onChange={setPriceType}>
            <option value="">All</option>
            <option value="Hourly">Per hour</option>
            <option value="Total">Total price</option>
          </CatalogFilterSelect>
        </CatalogFilterLayout>
      </div>
    </div>
  );
};

export default GigsPage;
